import { z } from 'zod';
import {
  BOOKING_STATUSES,
  BOOTH_TYPES,
  EVENT_TYPES,
  INQUIRY_SOURCES,
  ACCESSORY_ITEMS,
  ADDITIONAL_EXPENSE_NAMES,
  EDITING_STATUSES,
  EXPENSE_CURRENCIES,
  EXPENSE_OWNERS,
  PROJECT_EVENT_KINDS,
  PROJECT_EVENT_STATUSES,
  PROJECT_KEYS,
  VEHICLES,
  type BookingStatus,
  type EditingStatus,
  type ProjectEventKind,
  type ProjectEventStatus,
  type ProjectKey,
} from '../projects.js';

const text = (max: number) => z.string().trim().min(1).max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((value) => (value ? value : null));
const money = z.union([z.string(), z.number()]).transform((value) => String(value).trim());
const moneyString = money.refine((value) => /^-?\d+(\.\d{1,2})?$/u.test(value), 'Enter an amount');
const optionalMoney = z
  .union([z.string(), z.number(), z.null()])
  .optional()
  .transform((value) =>
    value === null || value === undefined || value === '' ? '0' : String(value).trim(),
  )
  .refine((value) => /^-?\d+(\.\d{1,2})?$/u.test(value), 'Enter an amount');
const uuid = z.string().uuid();

export const projectKeySchema = z.enum(PROJECT_KEYS);

// ---------------------------------------------------------------------------
// Calendar entries
// ---------------------------------------------------------------------------

export const createProjectEventSchema = z.object({
  title: text(200),
  kind: z.enum(PROJECT_EVENT_KINDS),
  startsAt: z.string().datetime({ offset: true }),
  endsAt: z.string().datetime({ offset: true }).nullish(),
  allDay: z.coerce.boolean().default(false),
  location: optionalText(300),
  notes: optionalText(4000),
  accountId: uuid.nullish(),
  /** Content shoots only. */
  editingStatus: z.enum(EDITING_STATUSES).nullish(),

  // Event bookings only.
  bookingStatus: z.enum(BOOKING_STATUSES).nullish(),
  boothType: z.enum(BOOTH_TYPES).nullish(),
  eventType: z.enum(EVENT_TYPES).nullish(),
  coordinatorName: optionalText(200),
  coordinatorPhone: optionalText(40),
  coordinatorEmail: z
    .string()
    .trim()
    .email('Enter an email address')
    .max(200)
    .nullish()
    .or(z.literal('').transform(() => null)),
  ownerId: uuid.nullish(),
  managerId: uuid.nullish(),
  leadId: uuid.nullish(),
  assist1Id: uuid.nullish(),
  assist2Id: uuid.nullish(),
  inquirySource: z.enum(INQUIRY_SOURCES).nullish(),
  inquiryDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/u, 'Pick a date')
    .nullish(),
});
export type CreateProjectEventInput = z.infer<typeof createProjectEventSchema>;

export const updateProjectEventSchema = createProjectEventSchema
  .omit({ kind: true })
  .partial()
  .extend({ status: z.enum(PROJECT_EVENT_STATUSES).optional() });
export type UpdateProjectEventInput = z.infer<typeof updateProjectEventSchema>;

export const projectEventsQuerySchema = z.object({
  from: z.string().datetime({ offset: true }),
  to: z.string().datetime({ offset: true }),
});

export interface ProjectEventView {
  id: string;
  project: ProjectKey;
  kind: ProjectEventKind;
  title: string;
  startsAt: string;
  endsAt: string | null;
  allDay: boolean;
  location: string | null;
  notes: string | null;
  status: ProjectEventStatus;
  editingStatus: EditingStatus | null;
  account: { id: string; name: string } | null;
  bookingStatus: BookingStatus | null;
  boothType: string | null;
}

export interface PersonRef {
  id: string;
  name: string;
}

/** One entry in full: the event page. */
export interface ProjectEventDetail extends ProjectEventView {
  eventType: string | null;
  coordinatorName: string | null;
  coordinatorPhone: string | null;
  coordinatorEmail: string | null;
  owner: PersonRef | null;
  manager: PersonRef | null;
  lead: PersonRef | null;
  assist1: PersonRef | null;
  assist2: PersonRef | null;
  inquirySource: string | null;
  inquiryDate: string | null;
}

// ---------------------------------------------------------------------------
// Photobooth event expense
// ---------------------------------------------------------------------------

export const additionalExpenseSchema = z.object({
  name: z.enum(ADDITIONAL_EXPENSE_NAMES),
  amount: moneyString,
});

export const createPhotoboothExpenseSchema = z.object({
  /** The name of the report (e.g. "Taj wedding – 12 Mar"). */
  expenseReport: text(200),
  currency: z.enum(EXPENSE_CURRENCIES),
  expenseOwner: z.enum(EXPENSE_OWNERS),

  gstInvoice: z.coerce.boolean(),
  /** The Invoice or GstInvoice the number was picked from. */
  invoiceId: uuid.nullish(),
  invoiceNumber: text(60),
  invoiceAmount: moneyString,

  eventDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, 'Pick a date'),
  eventLocation: optionalText(300),
  boothType: z.enum(BOOTH_TYPES).nullish(),
  businessManagerId: uuid.nullish(),
  accountId: uuid.nullish(),
  serviceType: optionalText(80),
  leadId: uuid.nullish(),
  assist1Id: uuid.nullish(),
  assist2Id: uuid.nullish(),
  driverId: uuid.nullish(),
  adminSalesId: uuid.nullish(),
  /** An event booking this expense is for, when there is one. */
  eventId: uuid.nullish(),

  additionalExpenses: z.array(additionalExpenseSchema).max(20).default([]),
  accessoryItem: z.enum(ACCESSORY_ITEMS).nullish(),
  accessoriesAmount: optionalMoney,
  plannerCommission: optionalMoney,
  assist1Amount: moneyString.default('1000'),
  assist2Amount: moneyString.default('1000'),
  vehicle: z.enum(VEHICLES).nullish(),
  transportationCost: optionalMoney,
});
export type CreatePhotoboothExpenseInput = z.infer<typeof createPhotoboothExpenseSchema>;

export interface PhotoboothExpenseView {
  id: string;
  expenseReport: string;
  currency: string;
  gstInvoice: boolean;
  invoiceNumber: string;
  invoiceAmount: string;
  eventDate: string;
  eventLocation: string | null;
  boothType: string | null;
  account: { id: string; name: string } | null;
  newAmount: string;
  totalExpense: string;
  totalProfit: string;
  /** The expense report it was sent for approval as, once it has been. */
  report: { id: string; number: string; status: string } | null;
}

/** One recorded expense in full: what the form was filled in with, and the figures worked out from it. */
export interface PhotoboothExpenseDetail extends PhotoboothExpenseView {
  expenseOwner: string;
  invoiceId: string | null;
  serviceType: string | null;
  eventId: string | null;
  businessManager: PersonRef | null;
  lead: PersonRef | null;
  assist1: PersonRef | null;
  assist2: PersonRef | null;
  driver: PersonRef | null;
  adminSales: PersonRef | null;
  additionalExpenses: { name: string; amount: string }[];
  accessoryItem: string | null;
  accessoriesAmount: string;
  plannerCommission: string;
  taxRate: string;
  taxAmount: string;
  leadAmount: string;
  adminSalesAmount: string;
  assist1Amount: string;
  assist2Amount: string;
  vehicle: string | null;
  transportationCost: string;
  submittedAt: string | null;
}

export const updatePhotoboothExpenseSchema = createPhotoboothExpenseSchema;
export type UpdatePhotoboothExpenseInput = CreatePhotoboothExpenseInput;

export interface InvoiceOption {
  id: string;
  number: string;
  amount: string;
  currency: string;
  date: string | null;
  account: { id: string; name: string } | null;
}

// ---------------------------------------------------------------------------
// Dashboards
// ---------------------------------------------------------------------------

export interface ProjectReminder {
  id: string;
  date: string;
  title: string;
  kind: ProjectEventKind;
  location: string | null;
}

/** The monthly figures, for the month shown and for the one before it. */
export interface PhotoboothMonthFigures {
  /** Confirmed and complete events dated in the month. */
  eventsBooked: number;
  /** Complete events dated in the month. */
  eventsCompleted: number;
  /** Bookings enquired about in the month: in discussion, confirmed or complete. */
  eventInquiries: number;
  incomeMonth: string;
  expenditureMonth: string;
  profitMonth: string;
}

export interface PhotoboothDashboard extends PhotoboothMonthFigures {
  project: 'PHOTOBOOTH_GOA';
  /** The month the figures are for, `YYYY-MM`. */
  month: string;
  /** The same figures a month earlier, for the up/down arrows. */
  previous: PhotoboothMonthFigures;
  expenditureYear: string;
  incomeYear: string;
  profitYear: string;
  recentExpenses: PhotoboothExpenseView[];
  reminders: ProjectReminder[];
}

export interface SocialDashboard {
  project: 'SOCIAL_MEDIA';
  month: string;
  shootsThisMonth: number;
  upcomingShoots: ProjectEventView[];
  /** Shot, but not yet edited. */
  toEdit: ProjectEventView[];
  /** Shot and edited. */
  done: ProjectEventView[];
  reminders: ProjectReminder[];
}

export interface PlainDashboard {
  project: 'WEDDINGS_DE_GOA';
  month: string;
  reminders: ProjectReminder[];
}

export type ProjectDashboard = PhotoboothDashboard | SocialDashboard | PlainDashboard;
